/**
 * useAuth — React hook for Better-Auth session state.
 *
 * Usage :
 *   const { user, isAuthenticated, isLoading, refresh, signout } = useAuth();
 *
 * On mount, calls fetchSession() (cookie-based). Exposes `refresh()` so
 * /auth/verify can re-poll after Better-Auth has set the session cookie.
 *
 * On successful session refresh, triggers SyncEngine.pullAll() and merges the
 * remote profile/consents/progression into localStorage (P5 L9). On signout,
 * resets the SyncEngine pending-push queue.
 */
import { useCallback, useEffect, useState } from 'react';
import { fetchSession, signOut as authSignOut } from '../lib/auth';
import { SyncEngine } from '../lib/sync';
import { applySyncedState } from '../lib/user-state';
import { applySyncedProgression } from '../lib/game-progression';
export function useAuth() {
    const [state, setState] = useState({
        user: null,
        isAuthenticated: false,
        isLoading: true,
    });
    const refresh = useCallback(async () => {
        setState((s) => ({ ...s, isLoading: true }));
        const session = await fetchSession();
        if (session) {
            try {
                const remote = await SyncEngine.pullAll();
                if (remote && remote.me) {
                    applySyncedState({ me: remote.me, consents: remote.consents });
                    applySyncedProgression(remote.progression);
                }
            }
            catch {
                // SyncEngine swallows network errors; this catch is a defensive belt-and-braces.
            }
        }
        setState({
            user: session?.user ?? null,
            isAuthenticated: Boolean(session),
            isLoading: false,
        });
    }, []);
    const signout = useCallback(async () => {
        await authSignOut();
        SyncEngine.reset();
        setState({ user: null, isAuthenticated: false, isLoading: false });
    }, []);
    useEffect(() => {
        void refresh();
    }, [refresh]);
    return { ...state, refresh, signout };
}
//# sourceMappingURL=useAuth.js.map